import clsx from 'clsx';
import React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import Icon from '../../atoms/Icon/Icon';
import useDraggableItemsList from './useDraggableItemsList';

/**
 * DraggableItemsList — v2 molecule.
 *
 * Wrapper genérico de drag & drop (sobre `react-beautiful-dnd`) sin lógica de
 * dominio: el consumidor inyecta el contenido de cada item vía `item.content`.
 * Renderiza una lista semántica `<ul>/<li>` y expone un asa de arrastre visual
 * (el icono `drag-bullets` con un `title` que el átomo traduce a `role="img"` +
 * `aria-label`).
 *
 * El arrastre se acota a la fila entera: `dragHandleProps` va en el `<li>`, no
 * en el asa. react-beautiful-dnd inyecta `role="button"` con el spread, así que
 * redeclaramos `role="listitem"` DESPUÉS del spread para preservar la semántica
 * de lista. El asa es un `<span>` NO interactivo a propósito: el filtro de rbd
 * rechaza iniciar un arrastre cuyo `event.target` sea interactivo (un
 * `<button>`), lo que rompería el arrastre en la mayor parte de la fila y
 * dejaría inerte el icono de arrastre interno de las tarjetas del consumidor.
 */

export type DraggableItem = {
  content: React.ReactNode;
  guid: string;
};

export type DraggableItemsListProps = {
  className?: string;
  disableDrag?: boolean;
  disableEditMode?: boolean;
  dragHandleAriaLabel?: string;
  handleOnDragEnd?: (result: DropResult) => void;
  items?: DraggableItem[];
  testId?: string;
};

const DraggableItemsList = React.forwardRef<HTMLDivElement, DraggableItemsListProps>(
  (
    {
      className,
      disableDrag = false,
      disableEditMode = true,
      dragHandleAriaLabel,
      handleOnDragEnd = () => {},
      items = [],
      testId,
    },
    ref,
  ) => {
    const { dragHandleLabel, isDragDisabled } = useDraggableItemsList({
      disableDrag,
      disableEditMode,
      dragHandleAriaLabel,
    });

    return (
      <div ref={ref} className={clsx('ui-draggable-items-list', className)} data-testid={testId}>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="droppable-general" type="droppableItem">
            {(droppableProvided, droppableSnapshot) => (
              // `role="list"` explícito recupera la semántica que WebKit/VoiceOver
              // pierde con `list-style: none` (reset global). Se omite en la lista
              // vacía: sin `<li>` hijos, el rol explícito violaría
              // `aria-required-children` (axe sólo exime el caso implícito).
              <ul
                className={clsx('ui-draggable-items-list__list', {
                  'ui-draggable-items-list__list--is-dragging-over': droppableSnapshot.isDraggingOver,
                })}
                ref={droppableProvided.innerRef}
                role={items.length ? 'list' : undefined}
                {...droppableProvided.droppableProps}
              >
                {items.map((item, index) => (
                  <Draggable draggableId={item.guid} index={index} isDragDisabled={isDragDisabled} key={item.guid}>
                    {(draggableProvided) => (
                      <li
                        className="ui-draggable-items-list__item"
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                        role="listitem"
                      >
                        {!isDragDisabled && (
                          <span className="ui-draggable-items-list__handle">
                            <Icon name="drag-bullets" size="md" title={dragHandleLabel} />
                          </span>
                        )}
                        <div className="ui-draggable-items-list__content">{item.content}</div>
                      </li>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  },
);

DraggableItemsList.displayName = 'DraggableItemsList';

export default DraggableItemsList;
